import i18n from './i18n';

/**
 * 语言同步工具
 * 用于在多个浏览器标签页之间同步语言设置
 */

type LanguageChangeCallback = (lang: string) => void;

let initialized = false;
let languageChangeListener: LanguageChangeCallback | null = null;

/**
 * 初始化语言同步功能
 * @param {Function} callback - 语言变化时的回调函数
 */
export const initLanguageSync = (callback: LanguageChangeCallback | null = null): void => {
  if (initialized) {
    return;
  }

  // 监听 localStorage 变化触发跨页签同步
  window.addEventListener('storage', handleStorageChange);
  initialized = true;

  // 如果提供了回调函数，保存它
  if (callback && typeof callback === 'function') {
    languageChangeListener = callback;
  }
};

/**
 * 处理localStorage变化
 * @param {StorageEvent} event - 存储事件对象
 */
const handleStorageChange = (event: StorageEvent): void => {
  if (event.key === 'i18nextLng' && event.newValue && event.newValue !== i18n.language) {
    // 更新当前标签页的语言
    i18n.changeLanguage(event.newValue);

    // 如果有回调函数，调用它
    if (languageChangeListener && typeof languageChangeListener === 'function') {
      languageChangeListener(event.newValue);
    }
  }
};

/**
 * 设置当前语言并同步到所有标签页
 * @param {string} lang - 语言代码
 */
export const setLanguage = (lang: string): void => {
  if (lang && lang !== i18n.language) {
    // 更改当前标签页的语言
    i18n.changeLanguage(lang);

    // 保存到 localStorage 以同步到其他标签页
    // 使用 setTimeout 确保在部分浏览器中触发 storage 事件
    setTimeout(() => {
      localStorage.setItem('i18nextLng', lang);
    }, 0);
  }
};

/**
 * 获取当前语言
 * @returns {string} 当前语言代码
 */
export const getCurrentLanguage = (): string => {
  return i18n.language;
};

/**
 * 添加语言变化监听器
 * @param {Function} listener - 语言变化时的监听函数
 */
export const addLanguageChangeListener = (listener: LanguageChangeCallback): void => {
  if (listener && typeof listener === 'function') {
    languageChangeListener = listener;
  }
};

/**
 * 移除语言变化监听器
 */
export const removeLanguageChangeListener = (): void => {
  languageChangeListener = null;
};

/**
 * 清理语言同步功能
 */
export const cleanupLanguageSync = (): void => {
  if (initialized) {
    window.removeEventListener('storage', handleStorageChange);
    initialized = false;
    languageChangeListener = null;
  }
};

export default {
  initLanguageSync,
  setLanguage,
  getCurrentLanguage,
  addLanguageChangeListener,
  removeLanguageChangeListener,
  cleanupLanguageSync,
};
